<script>
import fileTypes from '@/utils/fileTypes';

import FormBlock from '@/components/FormBlock.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import SelectableList from '@/components/ui/SelectableList.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import ProjectDescriptionAside from '@/components/project/ProjectDescriptionAside.vue';
import ProjectFileAttachStageTile from '@/components/project/ProjectFileAttachStageTile.vue';
import AgreementStatTiles from '@/components/agreement/AgreementStatTiles.vue';

export default {
  name: 'ProjectFileAttachList',
  components: {AgreementStatTiles, ProjectFileAttachStageTile, ProjectDescriptionAside, EmptyList, FileList, FormBlock, BaseCheckbox, SelectableList },
  props: {
    project: Object,
    stages: Array,
    tasks: Object,
    attaches: Array,
    external: Boolean,
  },
  data() {
    return {
      selectedStageIndexes: [],
    };
  },
  computed: {
    currUser() { return this.$store.state.auth?.user ?? {}; },
    foldedStages() { return this.currUser?.customSettings?.foldedStagesInAttaches ?? []; },
    projectId() { return this.external ? null : this.project.id; },
    attachesByTaskId() {
      return this.attaches
        .reduce((acc, val) => {
          const file = val.projectFile;
          file.isImage = fileTypes[file.file.extension]?.type === 'image';
          file.attachId = val.id;
          acc[val.task] ??= [];
          acc[val.task].push(file);
          return acc;
        }, {});
    },
    tasksWithAttachesByStageId() {
      const addAttaches = arr => arr
        .map(t => ({ ...t, attaches: this.attachesByTaskId[t['@id']] ?? [] }))
        .filter(t => t.attaches.length);


      const entries = Object.entries(this.tasks)
        .map(([stageId, tasks]) => [stageId, addAttaches(tasks)]);

      return Object.fromEntries(entries);
    },
    stagesCombined() {
      return this.stages
        .map(s => ({ ...s, tasks: this.tasksWithAttachesByStageId[s.id] }))
        .filter(s => s.tasks?.length);
    },
    stagesFiltered() {
      return this.selectedStageIndexes.map(i => this.stagesCombined[i]);
    },
    downloadHrefBase() {
      return this.external
        ? `/api/projects/ext/attaches/download/${this.$route.params.extId}/${this.$route.params.token}`
        : `/api/projects/${this.projectId}/attaches/download`;
    },
    downloadHrefAll() {
      const stages = this.stagesFiltered.map(s => `stages[]=${s.id}`).join('&');
      return `${this.downloadHrefBase}?${stages}`;
    },
  },
  watch: {
    stages: {
      immediate: true,
      handler() { this.selectAllStages(); },
    },
    tasks: {
      immediate: true,
      handler() { this.selectAllStages(); },
    },
    attaches: {
      immediate: true,
      handler() { this.selectAllStages(); },
    },
  },
  methods: {
    selectAllStages() {
      if (!this.stages.length) return;
      if (!Object.keys(this.tasks ?? {}).length) return;
      if (!this.attaches.length) return;
      this.$nextTick(() => {
        this.$refs.stageSelectableList?.selectAll(true);
      });
    },
  },
}
</script>

<template lang="pug">
  .b-project-file-attach-list
    EmptyList._empty-page(v-if="!attaches.length" label="В этом проекте пока нет закрепленных файлов")
    ._main(v-else)
      FormBlock(mobile v-for="stage in stagesFiltered" :key="stage.id")._stage
        ProjectFileAttachStageTile(:stage="stage" :external="external" :downloadHrefBase="downloadHrefBase" :project="project")
      slot(name="agreements")
    FormBlock(mobile v-if="attaches.length")._aside
      ._aside-title Разделы
      SelectableList._aside-stages(
        :items="stagesCombined"
        v-slot="{ items, checked, toggle, enter, leave, down }"
        ref="stageSelectableList"
        @change="selectedStageIndexes = $event"
      )
        ._aside-stage(v-for="(stage, i) in items" :key="stage.id" @mouseenter="enter(i)") {{ stage.name }}
          ._aside-stage-progress(:class="{ completed: stage.progress === 100 }") {{ stage.progress }}%
          BaseCheckbox(
            :value="checked.has(i)"
            @toggle="toggle(i, $event)"
            @mouseleave.native="leave(i)"
            @mousedown.native="down(i)"
          )
      Btn._download-all(:href="downloadHrefAll")
        Icon(name="download" colored)
        | Скачать файлы одним архивом
    FormBlock(mobile)._project-data
      ProjectDescriptionAside(:project="project")
</template>

<style scoped lang="scss">
.project-file-attach-list {
  display: grid;
  grid-template-columns: 1fr 400px;
  grid-template-rows: auto 1fr;
  gap: 40px;

  &__empty-page {
    grid-column: 1/-1;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 38px;
    grid-row: 1 / 2 span;
  }

  &__aside {
    align-self: start;

    &-title {
      padding-bottom: 22px;
      margin-bottom: 13px;
      border-bottom: 1px solid #EBEFF2;
    }

    &-stages {
      display: flex;
      flex-direction: column;
      gap: 16px;
      //margin-bottom: 30px;
    }

    &-stage {
      display: flex;
      flex-direction: row-reverse;
      justify-content: flex-end;
      align-items: center;
      padding: 16px 20px;
      background: #F8F9FC;
      border-radius: 6px;
      font-size: 14px;
    }

    &-stage-progress {
      color: #FFFFFF;
      background: #FFD059;
      width: 38px;
      height: 16px;
      border-radius: 4px;
      order: -1;
      font-size: 10px;
      font-weight: 700;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: auto;

      &.completed {
        background: #A5D192;
      }
    }
  }

  &__download-all {
    margin-top: 30px;
    width: 100%;
  }

  &__project-data {
    grid-column: 2;
    align-self: start;
  }

  @media (max-width: 1350px) {
    grid-template-columns: 1fr 300px;

    &__download-all {
      height: auto;
      padding: 10px 25px;

      svg {
        flex-shrink: 0;
      }
    }
  }

  @media (max-width: 1149.98px) {
    //grid-template-columns: 1fr 250px;
    gap: 30px;

    &__main {
      grid-row: 2;
      grid-column: 1 / 2 span;
    }

    &__aside {
      grid-row: 1;
      grid-column: 1;
    }

    &__project-data {
      grid-column: 2;
      grid-row: 1;
    }
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;

    &__main {
      grid-row: 2;
      grid-column: 1;
    }

    &__aside {
      grid-row: 1;
      grid-column: 1;
    }

    &__project-data {
      grid-column: 1;
      grid-row: 3;
    }
  }
}
</style>
